/**
 * OnCallSchedulePage — Frente N N3b: gestão da escala de plantão do operador.
 *
 * Rota: /admin/agenda/plantao. Gated `operator-oncall-schedule-read` (D-B: gate
 * próprio na página, além da query gateada no hook). Ativa o regime 3 (plantão)
 * na prática — sem shifts escalados, o gate N3a-1 só faz desconexão pura.
 *
 * Escrita gated `operator-oncall-schedule-manage` (`canManage`).
 */
import { CapabilityLockedBanner } from '@lume/ui-components';
import { CalendarClock } from 'lucide-react';

import { OnCallScheduleView } from '../features/oncall-schedule/OnCallScheduleView';
import { useCapability } from '../hooks/use-capability';
import { useOperatorOnCallShifts } from '../hooks/use-operator-on-call';

import type { JSX } from 'react';

export function OnCallSchedulePage(): JSX.Element {
  const canRead = useCapability('operator-oncall-schedule-read');
  const canManage = useCapability('operator-oncall-schedule-manage');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canRead) {
    return (
      <div className="flex h-full flex-col p-6">
        <CapabilityLockedBanner
          capability="operator-oncall-schedule-read"
          tierMin="solo"
          fallbackMessage="A escala de plantão não está habilitada para este tenant. Fale com o administrador para ativá-la."
        />
      </div>
    );
  }

  return <OnCallSchedulePageInner canManage={canManage} />;
}

function OnCallSchedulePageInner({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const shiftsQ = useOperatorOnCallShifts({}, true);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header className="flex items-start gap-3 border-b border-border-subtle bg-surface px-6 py-4">
        <CalendarClock size={22} aria-hidden className="mt-0.5 text-text-secondary" />
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-text-secondary">
            Coordenação
          </div>
          <h1 className="text-xl font-semibold text-text-primary">Escala de plantão</h1>
        </div>
      </header>

      <div className="px-6 py-6">
        {shiftsQ.isLoading ? (
          <p className="text-sm text-text-secondary">Carregando a escala…</p>
        ) : shiftsQ.isError ? (
          <p role="alert" className="text-sm text-red-600">
            Falha ao carregar a escala de plantão.
          </p>
        ) : (
          <OnCallScheduleView shifts={shiftsQ.data?.data ?? []} canManage={canManage} />
        )}
      </div>
    </div>
  );
}
